import React from 'react';
import { motion } from 'framer-motion';
import { Building2, RefreshCw, MonitorSpeaker, Satellite, Zap, ArrowRight, PhoneCall, Heater, AlertTriangle, ClipboardCheck } from 'lucide-react';

const services = [
  {
    icon: Building2,
    title: 'Altbauinstallationen',
    problem: 'Veraltete Elektrik, unsichere Leitungen und fehlende Steckdosen im Altbau?',
    solution: 'Wir sanieren Ihre komplette Elektroinstallation fachgerecht und normgerecht.',
    benefit: 'Mehr Sicherheit und Komfort bei voller Werterhaltung Ihrer Immobilie.',
  },
  {
    icon: RefreshCw,
    title: 'Modernisierungen',
    problem: 'Ihre Elektroinstallation entspricht nicht mehr den aktuellen Anforderungen?',
    solution: 'Wir bringen Ihre Elektrik auf den neuesten Stand – effizient und zukunftssicher.',
    benefit: 'Zeitgemäße Technik, höhere Sicherheit und geringere Energiekosten.',
  },
  {
    icon: MonitorSpeaker,
    title: 'Sprechanlagen von Legrand-Bticino',
    problem: 'Veraltete Klingelanlage ohne Video- oder Zugangsfunktion?',
    solution: 'Planung, Installation und Erweiterung moderner Audio- und Video-Sprechanlagen – inklusive Zugangskontrolle, Infomodulen und 2-Draht-Bus-Technologie.',
    benefit: 'Sehen, hören und steuern, wer Zutritt erhält – komfortabel und sicher.',
  },
  {
    icon: Satellite,
    title: 'SAT- & Empfangsanlagen',
    problem: 'Schlechter Empfang oder veraltete Infrastruktur?',
    solution: 'Professionelle Installation von Satelliten- und Empfangsanlagen.',
    benefit: 'Kristallklares Bild und perfekter Empfang in jedem Raum.',
  },
  {
    icon: Heater,
    title: 'Elektrospeicher- & Nachtspeicherheizungen',
    problem: 'Probleme oder Störungen mit Ihrer Nachtspeicherheizung?',
    solution: 'Wir haben jahrzehntelange Erfahrung mit Nachtspeicherheizungen – sprechen Sie uns bei Problemen oder Störungen gerne an.',
    benefit: 'Schnelle Diagnose und fachgerechte Reparatur durch erfahrene Spezialisten.',
  },
  {
    icon: AlertTriangle,
    title: 'Rauchwarnmelder nach DIN 14676',
    problem: 'Keine oder veraltete Rauchwarnmelder in Ihren Räumen?',
    solution: 'Normgerechte Installation und Wartung von Rauchwarnmeldern nach DIN 14676.',
    benefit: 'Gesetzliche Pflicht erfüllt und maximaler Schutz für Ihre Familie.',
  },
  {
    icon: ClipboardCheck,
    title: 'E-Check',
    problem: 'Wann wurde Ihre Elektroanlage zuletzt geprüft?',
    solution: 'Professionelle Prüfung aller elektrischen Anlagen nach aktuellen Normen.',
    benefit: 'Sicherheit, Versicherungsschutz und Werterhaltung Ihrer Immobilie.',
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function LeistungenSection() {
  const scrollToKontakt = () => {
    const el = document.querySelector('#kontakt');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="leistungen" data-testid="leistungen-section" className="py-24 sm:py-32 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 sm:mb-20"
        >
          <span className="text-[#10B981] font-['Manrope'] text-sm font-semibold tracking-[0.2em] uppercase">
            Unsere Leistungen
          </span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-[#1A3A22] tracking-tight mt-3 mb-4">
            Elektroinstallationen in Bonn – <span className="text-[#2D5A27]">Lösungen, die funktionieren</span>
          </h2>
          <p className="font-['Manrope'] text-gray-600 text-base sm:text-lg max-w-xl mx-auto">
            Professionelle Elektroarbeiten für Altbau, Neubau und Gewerbe in Bonn und dem Rhein-Sieg-Kreis – von der Planung bis zur Abnahme.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {services.map((service, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              data-testid={`service-card-${idx}`}
              className={`card-3d p-8 rounded-none border transition-all duration-400 ${
                idx === 0
                  ? 'md:col-span-2 bg-[#1A3A22] text-white border-[#2D5A27]/30'
                  : 'bg-white border-gray-100 hover:border-[#2D5A27]/30'
              }`}
            >
              <div className={`w-14 h-14 rounded-lg flex items-center justify-center mb-6 ${
                idx === 0 ? 'bg-[#10B981]/20' : 'bg-[#F4F8F4]'
              }`}>
                <service.icon
                  className={`w-7 h-7 ${idx === 0 ? 'text-[#10B981]' : 'text-[#2D5A27]'}`}
                  strokeWidth={1.5}
                />
              </div>
              <h3 className={`font-['Outfit'] text-xl sm:text-2xl font-semibold mb-4 leading-tight ${
                idx === 0 ? 'text-white' : 'text-[#1A3A22]'
              }`}>
                {service.title}
              </h3>
              <p className={`font-['Manrope'] text-sm mb-3 ${
                idx === 0 ? 'text-white/60' : 'text-gray-500'
              }`}>
                {service.problem}
              </p>
              <p className={`font-['Manrope'] text-base mb-3 ${
                idx === 0 ? 'text-white/90' : 'text-gray-700'
              }`}>
                {service.solution}
              </p>
              <div className={`flex items-start gap-2 pt-3 border-t ${
                idx === 0 ? 'border-white/10' : 'border-gray-100'
              }`}>
                <ArrowRight className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                  idx === 0 ? 'text-[#10B981]' : 'text-[#2D5A27]'
                }`} />
                <span className={`font-['Manrope'] text-sm font-medium ${
                  idx === 0 ? 'text-[#10B981]' : 'text-[#2D5A27]'
                }`}>
                  {service.benefit}
                </span>
              </div>

              {/* Bticino-Logo nur bei Sprechanlagen-Kachel */}
              {service.title.includes('Legrand-Bticino') && (
                <div className="flex items-center gap-4 mt-6 pt-5 border-t border-gray-100" data-testid="bticino-badge">
                  <span className="font-['Manrope'] text-xs text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Technik von
                  </span>
                  <img
                    src="/bticino-logo.png"
                    alt="Bticino Logo für Sprechanlagen von Legrand-Bticino"
                    className="h-10 sm:h-12 w-auto object-contain"
                  />
                </div>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* Special Box - Quick Contact */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-10 bg-gradient-to-r from-[#2D5A27] to-[#1A3A22] p-8 sm:p-10 flex flex-col sm:flex-row items-center justify-between gap-6"
          data-testid="special-box"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#10B981]/20 rounded-full flex items-center justify-center flex-shrink-0">
              <PhoneCall className="w-7 h-7 text-[#10B981]" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-['Outfit'] text-xl sm:text-2xl font-bold text-white">
                Schnelle Beratung gewünscht?
              </h3>
              <p className="font-['Manrope'] text-white/70 text-sm sm:text-base">
                Rufen Sie uns direkt an oder stellen Sie eine Anfrage – wir melden uns umgehend.
              </p>
            </div>
          </div>
          <button
            onClick={scrollToKontakt}
            data-testid="special-box-cta"
            className="bg-white hover:bg-[#10B981] text-[#1A3A22] hover:text-white px-8 py-4 font-['Outfit'] font-semibold text-sm tracking-[0.1em] uppercase transition-all duration-300 hover:-translate-y-1 whitespace-nowrap flex-shrink-0"
          >
            Anfrage stellen
          </button>
        </motion.div>

        {/* Allgemeine Elektroarbeiten - Full width banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="mt-6 border border-gray-100 p-8 sm:p-10 flex flex-col md:flex-row items-center gap-8"
          data-testid="allgemeine-elektro-banner"
        >
          <div className="w-16 h-16 bg-[#F4F8F4] rounded-lg flex items-center justify-center flex-shrink-0">
            <Zap className="w-8 h-8 text-[#2D5A27]" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <h3 className="font-['Outfit'] text-2xl font-bold text-[#1A3A22] mb-2">
              Allgemeine Elektroarbeiten, Kundendienst/Service
            </h3>
            <p className="font-['Manrope'] text-gray-600 text-base leading-relaxed">
              Von der defekten Steckdose oder Beleuchtung bis zur kompletten Neuverkabelung:
              Wir übernehmen Elektroarbeiten im Bestand, Modernisierungen, Reparaturen und
              zuverlässigen Kundendienst für Privatkunden, Hausverwaltungen und Gewerbe.
            </p>
          </div>
          <button
            onClick={scrollToKontakt}
            data-testid="allgemeine-elektro-cta"
            className="bg-[#2D5A27] hover:bg-[#10B981] text-white px-8 py-4 font-['Outfit'] font-semibold text-sm tracking-[0.1em] uppercase transition-all duration-300 hover:-translate-y-1 whitespace-nowrap flex-shrink-0"
          >
            Anfrage stellen
          </button>
        </motion.div>
      </div>
    </section>
  );
}
